import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApp, useAsync } from '../lib/app-context';
import { Async, Badge, Card, Empty, Field, NumberField, SelectField, TextField } from '../components/ui';
import { DeleteButton } from './Exercises';
import type { Difficulty, Exercise, SkillStudy, SkillStudyStep } from '../lib/types';

const DIFFICULTIES: Difficulty[] = ['Beginner', 'Intermediate', 'Advanced'];

/** The flat technique library — one video plus the SP-DOC-02 captioned step template. */
export function Studies() {
    const { moduleId } = useParams();
    const { data } = useApp();
    const state = useAsync(async () => ({
        studies: await data.listSkillStudies(moduleId!),
        exercises: await data.listExercises(moduleId!),
    }), [moduleId]);
    const [editingId, setEditingId] = useState<string | 'new' | null>(null);

    return (
        <Async state={state}>
            {({ studies, exercises }) => {
                if (editingId) {
                    const study = editingId === 'new' ? null : studies.find((s) => s.id === editingId) ?? null;
                    return (
                        <StudyEditor
                            key={editingId}
                            moduleId={moduleId!}
                            study={study}
                            exercises={exercises}
                            nextSort={studies.length}
                            onDone={() => { setEditingId(null); state.reload(); }}
                            onCancel={() => setEditingId(null)}
                        />
                    );
                }

                return (
                    <>
                        <div className="page-head">
                            <div>
                                <h1>Skill studies</h1>
                                <p className="muted">
                                    Individual technique lessons. These are what the app calls “Skills” —
                                    a video and a step breakdown, not the progression tree.
                                </p>
                            </div>
                            <button className="btn" onClick={() => setEditingId('new')}>New study</button>
                        </div>

                        {studies.length === 0 ? (
                            <Empty title="No skill studies yet" />
                        ) : (
                            <div className="list">
                                {studies.map((study) => (
                                    <div key={study.id} className="list-row">
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div className="title">{study.title}</div>
                                            <div className="small muted">
                                                {Math.floor(study.duration_seconds / 60)}:
                                                {String(Math.round(study.duration_seconds % 60)).padStart(2, '0')}
                                                {' · '}{study.steps.length} steps
                                            </div>
                                        </div>
                                        {study.is_free_sample && <Badge tone="teal">Free</Badge>}
                                        <Badge>{study.difficulty}</Badge>
                                        {study.steps.length !== 4 && (
                                            <Badge tone="coral">{study.steps.length}/4 panels</Badge>
                                        )}
                                        <button className="btn secondary small"
                                            onClick={() => setEditingId(study.id)}>Edit</button>
                                        <DeleteButton label={study.title}
                                            onDelete={() => data.deleteSkillStudy(study.id)}
                                            onDone={state.reload} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                );
            }}
        </Async>
    );
}

function StudyEditor({ moduleId, study, exercises, nextSort, onDone, onCancel }: {
    moduleId: string; study: SkillStudy | null; exercises: Exercise[]; nextSort: number;
    onDone: () => void; onCancel: () => void;
}) {
    const { data } = useApp();
    const [form, setForm] = useState<Omit<SkillStudy, 'id' | 'module_id'>>({
        slug: study?.slug ?? '',
        title: study?.title ?? '',
        difficulty: study?.difficulty ?? 'Intermediate',
        coaching_notes: study?.coaching_notes ?? '',
        duration_seconds: study?.duration_seconds ?? 90,
        steps: study?.steps ?? [],
        exercise_id: study?.exercise_id ?? null,
        is_free_sample: study?.is_free_sample ?? false,
        sort_order: study?.sort_order ?? nextSort,
    });
    const [error, setError] = useState<string | null>(null);

    const update = (patch: Partial<typeof form>) => setForm({ ...form, ...patch });

    function updateStep(index: number, step: SkillStudyStep) {
        update({ steps: form.steps.map((s, i) => (i === index ? step : s)) });
    }

    async function save() {
        setError(null);
        try {
            await data.upsertSkillStudy({
                ...(study ? { id: study.id } : {}),
                module_id: moduleId,
                ...form,
                slug: form.slug || form.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
            });
            onDone();
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    }

    return (
        <>
            <div className="page-head">
                <div>
                    <div className="eyebrow">Skill study</div>
                    <h1>{form.title || 'New study'}</h1>
                </div>
                <div className="row">
                    <button className="btn secondary" onClick={onCancel}>Cancel</button>
                    <button className="btn" disabled={!form.title} onClick={save}>Save study</button>
                </div>
            </div>

            {error && <div className="banner error" style={{ marginBottom: 16 }}>{error}</div>}

            <Card>
                <div className="stack">
                    <div className="field-row">
                        <TextField label="Title" value={form.title} onChange={(v) => update({ title: v })} />
                        <TextField label="Slug" value={form.slug} onChange={(v) => update({ slug: v })}
                            placeholder="derived from title" />
                    </div>
                    <TextField label="Coaching notes" multiline value={form.coaching_notes}
                        onChange={(v) => update({ coaching_notes: v })} />
                    <div className="field-row-3">
                        <SelectField label="Difficulty" value={form.difficulty}
                            onChange={(v) => update({ difficulty: v })}
                            options={DIFFICULTIES.map((d) => ({ value: d, label: d }))} />
                        <NumberField label="Duration (seconds)" value={form.duration_seconds}
                            onChange={(v) => update({ duration_seconds: v })} />
                        <SelectField
                            label="Teaches exercise"
                            value={form.exercise_id ?? ''}
                            placeholder="None"
                            onChange={(v) => update({ exercise_id: v || null })}
                            options={exercises.map((e) => ({ value: e.id, label: e.name }))}
                        />
                    </div>
                    <Field label="Free-tier access">
                        <label className="row-tight" style={{ fontWeight: 500 }}>
                            <input type="checkbox" checked={form.is_free_sample}
                                onChange={(e) => update({ is_free_sample: e.target.checked })} />
                            Available on the Free tier
                        </label>
                    </Field>
                </div>
            </Card>

            <div style={{ marginTop: 20 }}>
                <Card>
                    <div className="row">
                        <h2>Step panels</h2>
                        <div className="spacer" />
                        <span className="small muted">
                            SP-DOC-02 specifies four 1:1 panels, captions max two lines
                        </span>
                    </div>

                    {form.steps.length !== 4 && (
                        <div className="banner" style={{ marginTop: 12 }}>
                            The app renders a 2×2 grid, so four panels is the designed shape.
                            You currently have {form.steps.length}.
                        </div>
                    )}

                    <div className="stack" style={{ marginTop: 12 }}>
                        {form.steps.map((step, index) => (
                            <div key={index} className="prescription">
                                <div className="row">
                                    <span className="badge coral">{index + 1}</span>
                                    <input
                                        style={{ flex: 1 }}
                                        value={step.caption}
                                        placeholder="Clinical instruction, max two lines"
                                        onChange={(e) => updateStep(index, { ...step, caption: e.target.value })}
                                    />
                                    <input
                                        style={{ maxWidth: 140 }}
                                        value={step.mediaSeed}
                                        placeholder="media ref"
                                        onChange={(e) => updateStep(index, { ...step, mediaSeed: e.target.value })}
                                    />
                                    <button className="btn icon danger" aria-label="Remove step"
                                        onClick={() => update({ steps: form.steps.filter((_, i) => i !== index) })}>×</button>
                                </div>
                                {step.caption.length > 90 && (
                                    <p className="small" style={{ color: 'var(--error-800)', marginTop: 6 }}>
                                        Long captions truncate at two lines in the app.
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>

                    <button
                        className="btn secondary small"
                        style={{ marginTop: 12 }}
                        onClick={() => update({
                            steps: [...form.steps, { caption: '', mediaSeed: `step-${form.steps.length + 1}` }],
                        })}
                    >Add panel</button>
                </Card>
            </div>
        </>
    );
}
